<template>
  <div class="socialMetrics"
    v-loading="loading"
  >
    <div class="socialMetrics-title">
      <h3>SOCIAL ACTIONS</h3>
    </div>
    <div class="socialMetrics-chart">
      <div id="socialChart"></div>
      <div class="socialMetrics-hero">
        <span>{{ heroStat }}</span>
      </div>
    </div>
    <div class="socialMetrics-table">
      <neb-small-table
        v-show="tableData.length > 0"
        :columns="[ 'ACTION', '#', '%' ]"
        :data="tableData"
      />
      <div class="socialMetrics-noData"
        v-show="tableData.length < 1 && !loading"
      >
        <neb-icon name="x"
          height="100px"
          width="100px"
        />
        <span>Data Unavailable</span>
      </div>
    </div>
  </div>
</template>

<script>
import { useDonutChart } from '@/features'
import { computed, ref, onMounted, watch } from '@vue/composition-api'
import api from '@/api'
import endpoints from '@/api/endpoints'
import NebSmallTable from '@/components/Nebula/layout/SmallTable'
import { formatPercent, abbreviateNumber } from '@/components/Nebula/formatting.js'
import NebIcon from '@/components/Nebula/Icon'

const chartColors = [ '#d1a8fc', '#a063e0', '#792dc8' ]

export default {
  name: 'SocialMetrics',

  components: {
    NebSmallTable,
    NebIcon
  },

  props: {
    pageUrlId: {
      type: String,
      required: true
    },
    brandIdProp: {
      type: [Number, String],
      required: true
    },
    metricDateRange: {
      type: Array,
      default: () => [ '-10000', '0' ]
    }
  },

  setup (props) {
    // State
    const metricData = ref(null)
    const loading = ref(false)

    // Chart
    const chart = useDonutChart('socialChart', {
      encoding: {
        tooltip: [
          { field: 'category', type: 'nominal', title: 'Category' },
          { field: 'value', type: 'quantitative', title: 'Count' }
        ],
        color: {
          scale: {
            domain: [ 'Like', 'Comment', 'Share' ],
            range: chartColors
          }
        }
      }
    })
    const chartData = computed(() => {
      if (metricData.value === null) {
        return []
      }
      return metricData.value.chartData
        .map(obj => ({
          category: obj.label,
          value: obj.value
        }))
    })

    // Hero Stat
    const heroStat = computed(() => {
      if (metricData.value === null) {
        return '--'
      }

      return abbreviateNumber(metricData.value.heroStat.value)
    })

    // Table
    const tableData = computed(() => {
      if (metricData.value === null) {
        return []
      }
      return metricData.value.tableData
        .map((item, idx) => ({
          'ACTION': item[0].value,
          '#': item[1].value.toLocaleString(),
          '%': formatPercent(item[2].value),
          color: chartColors[idx]
        }))
    })

    // Methods
    const loadData = async (refetch = false) => {
      if (metricData.value !== null && !refetch) {
        return
      }
      loading.value = true
      const params = {
        metricDateRange: props.metricDateRange,
        brandId: props.brandIdProp
      }
      const resp = await api.get(endpoints.assets.socialStats(props.pageUrlId), { params })
      metricData.value = resp.data
      loading.value = false
    }

    watch(() => props.metricDateRange, async () => {
      await loadData(true)
      chart.setData(chartData.value)
      await chart.render()
    })

    // Hooks
    onMounted(async () => {
      await loadData()
      chart.setData(chartData.value)
      await chart.render()
    })

    return {
      tableData,
      heroStat,
      loading
    }
  }
}
</script>

<style lang="scss">
@import '../Nebula/style.scss';

#socialChart {
  height: 210px;
}

.socialMetrics {
  display: flex;
  width: 250px;
  flex-direction: column;
  align-items: center;
  height: 430px;

  &-noData {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .nebIcon {
      color: $warm-grey;
    }

    span {
      font-family: $defaultFont;
      color: $warm-grey;
      font-size: 14px;
      display: block;
    }
  }

  &-title {
    width: 100%;

    h3 {
      margin: 0;
      font-family: $defaultFont;
      color: $slate-grey;
      font-weight: 300;
      font-size: 14px;
    }
  }

  &-chart {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }

  &-hero {
    position: absolute;
    margin: auto;

    span {
      font-family: $defaultFont;
      font-size: 20px;
      color: $slate-grey;
      font-weight: bold;
    }
  }

  &-table {
    width: 100%;
  }
}
</style>
